import React from 'react';
import { Box } from '@mui/material';
import { MediaHandlerProps, ProcessedMediaData } from './types';

export const useMediaHandler = ({ 
  imageFile, 
  imageUrl, 
  imageDescription 
}: MediaHandlerProps): ProcessedMediaData => {
  // Convert File to URL for display if imageFile is provided
  const displayImageUrl = imageFile ? URL.createObjectURL(imageFile) : imageUrl;
  
  // Handle different imageDescription formats
  const altText = typeof imageDescription === 'string' 
    ? imageDescription 
    : imageDescription?.alt || "Uploaded image";
  
  const notableDeviations = typeof imageDescription === 'object'
    ? imageDescription?.notableDeviations || []
    : [];

  return {
    displayImageUrl: displayImageUrl || undefined,
    altText,
    notableDeviations
  };
};

interface MediaDisplayProps {
  src: string;
  alt: string;
  sx?: object;
  component?: React.ElementType;
}

export const MediaDisplay: React.FC<MediaDisplayProps> = ({ 
  src, 
  alt, 
  sx = {},
  component = "img"
}) => {
  return (
    <Box 
      component={component}
      src={src}
      alt={alt}
      sx={sx}
    />
  );
};

// Generic image display component for social media posts
export const SocialMediaImage: React.FC<{
  src: string;
  alt: string;
  platform: string;
}> = ({ src, alt, platform }) => {
  const getImageStyles = () => {
    switch (platform.toLowerCase()) {
      case 'twitter':
      case 'x':
        return {
          width: '100%',
          maxHeight: '300px',
          objectFit: 'cover',
          borderRadius: 2,
          mt: 1.5,
          border: '1px solid #eff3f4'
        };
      case 'facebook':
        return {
          width: '100%',
          maxHeight: '350px',
          objectFit: 'cover'
        };
      case 'instagram':
        return {
          width: '100%',
          maxHeight: '450px',
          objectFit: 'cover'
        };
      case 'linkedin':
        return {
          width: '100%',
          maxHeight: '400px',
          objectFit: 'cover'
        };
      case 'reddit':
        return {
          width: '100%',
          maxHeight: '400px',
          objectFit: 'contain',
          bgcolor: '#f5f5f5'
        };
      case 'youtube':
        return {
          width: '100%',
          height: '180px',
          objectFit: 'cover'
        };
      case 'tiktok':
        return {
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          position: 'absolute',
          opacity: 0.9
        };
      default:
        return {
          width: '100%',
          maxHeight: '300px',
          objectFit: 'cover',
          borderRadius: 2
        };
    }
  };

  return (
    <MediaDisplay
      src={src}
      alt={alt}
      sx={getImageStyles()}
    />
  );
};